import type { ThirdPartyFulfillmentBudget } from "@redotech/admin-sdk/rpc/schema/inventory/get-third-party-fulfillment-budgets";
import { RedoButton } from "@redotech/arbiter/buttons/redo-button";
import { Flex } from "@redotech/arbiter/flex";
import { RedoTextInput } from "@redotech/arbiter/input/redo-text-input";
import type { RedoListItem } from "@redotech/arbiter/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/arbiter/select-dropdown/redo-single-select-dropdown-input";
import { Text } from "@redotech/arbiter/text";
import { RedoToggle } from "@redotech/arbiter/toggle/redo-toggle";
import {
  THIRD_PARTY_FULFILLMENT_BUDGET_DAYS,
  ThirdPartyFulfillmentWeeklyOrderLimits,
} from "@redotech/redo-fulfillment-common/third-party-fulfillment-budget";
import CalendarIcon from "@redotech/redo-icons/calendar.svg";
import { toast } from "@redotech/redo-web/alert";
import { useRpcMutation } from "@redotech/redo-web/rpc-query/use-rpc-mutation";
import { useRpcQuery } from "@redotech/redo-web/rpc-query/use-rpc-query";
import { memo, useMemo, useState } from "react";
import { RedoAdminRpcClientContext } from "../../../app/redo-admin-rpc-client-provider";
import { StepCard } from "./component/step-card";

interface BudgetDraft {
  enabled: boolean;
  limits: string[];
  revision: string;
  requestId: string;
}

interface ThirdPartyFulfillmentBudgetCardProps {
  merchantTeamId: string;
}

function editBudget(warehouse: ThirdPartyFulfillmentBudget): BudgetDraft {
  return {
    enabled: warehouse.enabled,
    limits: warehouse.limits.map((limit) =>
      limit === null ? "" : String(limit),
    ),
    revision: warehouse.revision,
    requestId: crypto.randomUUID(),
  };
}

function getBudgetStatusDetail({
  warehouse,
  hasDraft,
}: {
  warehouse: ThirdPartyFulfillmentBudget;
  hasDraft: boolean;
}): string {
  if (hasDraft) {
    return "Unsaved changes · Status reflects saved limits";
  }
  if (warehouse.paused) {
    return "Next reopening push: warehouse-local midnight";
  }
  if (warehouse.limit === null) {
    return "Orders are still counted";
  }
  return `${Math.max(0, warehouse.limit - warehouse.count).toLocaleString()} orders remaining today`;
}

export const ThirdPartyFulfillmentBudgetCard = memo(
  function ThirdPartyFulfillmentBudgetCard({
    merchantTeamId,
  }: ThirdPartyFulfillmentBudgetCardProps) {
    const [open, setOpen] = useState(false);
    const [selectedId, setSelectedId] = useState<string>();
    const [drafts, setDrafts] = useState<Map<string, BudgetDraft>>(new Map());
    const args = useMemo(() => ({ merchantTeamId }), [merchantTeamId]);
    const query = useRpcQuery(
      RedoAdminRpcClientContext,
      "getThirdPartyFulfillmentBudgets",
      args,
    );
    const mutation = useRpcMutation(RedoAdminRpcClientContext, {
      method: "saveThirdPartyFulfillmentBudget",
    });
    const warehouses = query.data?.warehouses ?? [];
    const warehouse =
      warehouses.find((item) => item.sourceTeamId === selectedId) ??
      warehouses[0];
    const draft = warehouse ? drafts.get(warehouse.sourceTeamId) : undefined;
    const limits =
      draft?.limits ??
      warehouse?.limits.map((limit) => (limit === null ? "" : String(limit))) ??
      [];
    const enabled = draft?.enabled ?? warehouse?.enabled ?? false;
    const options: RedoListItem<string>[] = warehouses.map((item) => ({
      type: "text",
      id: item.sourceTeamId,
      value: item.sourceTeamId,
      text: item.name,
    }));
    const configured = warehouses.filter(
      (item) => item.enabled && item.limits.some((limit) => limit !== null),
    ).length;

    const change = (
      changes: Partial<Pick<BudgetDraft, "enabled" | "limits">>,
    ) => {
      if (!warehouse) {
        return;
      }
      const id = warehouse.sourceTeamId;
      setDrafts((previous) =>
        new Map(previous).set(id, {
          ...(previous.get(id) ?? editBudget(warehouse)),
          ...changes,
          requestId: crypto.randomUUID(),
        }),
      );
    };
    const discard = () => {
      if (!warehouse) {
        return;
      }
      setDrafts((previous) => {
        const next = new Map(previous);
        next.delete(warehouse.sourceTeamId);
        return next;
      });
      void query.refetch();
    };
    const save = async () => {
      if (!warehouse || !draft) {
        return;
      }
      const parsed = ThirdPartyFulfillmentWeeklyOrderLimits.safeParse(
        draft.enabled
          ? draft.limits.map((limit) =>
              limit.trim() === "" ? null : Number(limit),
            )
          : warehouse.limits,
      );
      if (!parsed.success) {
        return;
      }
      const id = warehouse.sourceTeamId;
      try {
        await mutation.mutateAsync({
          merchantTeamId,
          sourceTeamId: id,
          enabled: draft.enabled,
          limits: parsed.data,
          expectedRevision: draft.revision,
          requestId: draft.requestId,
        });
        setDrafts((previous) => {
          const next = new Map(previous);
          next.delete(id);
          return next;
        });
        await query.refetch();
        toast("Daily limit saved. Inventory update queued.", {
          variant: "success",
        });
      } catch (error: unknown) {
        toast(
          error instanceof Error
            ? error.message
            : "Could not save daily limit. Your edits are preserved.",
          { variant: "error" },
        );
      }
    };
    const valid =
      !enabled ||
      ThirdPartyFulfillmentWeeklyOrderLimits.safeParse(
        limits.map((limit) => (limit.trim() === "" ? null : Number(limit))),
      ).success;

    return (
      <StepCard
        description={
          configured
            ? `${configured} warehouse${configured === 1 ? "" : "s"} with a daily limit`
            : "Not configured"
        }
        fullWidthBody
        IconComponent={CalendarIcon}
        onToggle={() => setOpen(!open)}
        open={open}
        title="Daily refulfillment limit"
      >
        <Flex dir="column" gap="none">
          <Flex dir="column" gap="lg" px="2xl" py="xl">
            {query.isPending && (
              <Text fontSize="xs">Loading daily limits…</Text>
            )}
            {query.isError && (
              <Flex dir="column" gap="sm">
                <Text fontSize="xs" textColor="error">
                  Could not load daily limits.
                </Text>
                <RedoButton
                  hierarchy="secondary"
                  onClick={() => void query.refetch()}
                  size="sm"
                  text="Retry"
                />
              </Flex>
            )}
            {!query.isPending && !query.isError && warehouses.length === 0 && (
              <Text fontSize="xs" textColor="secondary">
                Link an RMR warehouse to configure a daily limit.
              </Text>
            )}
            {warehouse && (
              <>
                <Flex
                  align="flex-end"
                  gap="lg"
                  justify="space-between"
                  wrap="wrap"
                >
                  <Flex dir="column" maxW="full" minW="none" w="xxs">
                    <RedoSingleSelectDropdownInput
                      disabled={mutation.isPending}
                      fullWidth
                      label="RMR warehouse"
                      options={options}
                      optionSelected={({ item }) =>
                        setSelectedId(String(item.id))
                      }
                      selectedItem={options.find(
                        (item) => item.id === warehouse.sourceTeamId,
                      )}
                      size="xs"
                    />
                  </Flex>
                  <Flex align="center" gap="md" pb="xs">
                    <RedoToggle
                      disabled={
                        mutation.isPending ||
                        (Boolean(warehouse.configurationError) && !enabled)
                      }
                      label="Limit daily orders"
                      labelPosition="left"
                      setValue={(value) => change({ enabled: value })}
                      size="sm"
                      value={enabled}
                    />
                  </Flex>
                </Flex>
                {warehouse.configurationError ? (
                  <Text fontSize="xs" textColor="error">
                    {warehouse.configurationError}
                  </Text>
                ) : (
                  <Text fontSize="xxs" textColor="tertiary">
                    {warehouse.timezone} · Shared across warehouse locations
                  </Text>
                )}
                {enabled ? (
                  <Flex dir="column" gap="sm" pt="xs">
                    <Flex gap="xs" wrap="wrap">
                      {THIRD_PARTY_FULFILLMENT_BUDGET_DAYS.map((day, index) => (
                        <Flex
                          basis="var(--redo-spacing-8xl)"
                          dir="column"
                          key={day}
                          minW="none"
                          shrink={0}
                        >
                          <RedoTextInput
                            ariaLabel={`${day} order limit`}
                            fullWidth
                            inputMode="numeric"
                            inputTypeHint="number"
                            label={day.slice(0, 3)}
                            max={1_000_000}
                            min={1}
                            placeholder="—"
                            setValue={(value) =>
                              change({
                                limits: limits.map((limit, i) =>
                                  i === index ? value : limit,
                                ),
                              })
                            }
                            size="sm"
                            state={
                              mutation.isPending ||
                              Boolean(warehouse.configurationError)
                                ? "disabled"
                                : "default"
                            }
                            step={1}
                            value={limits[index] ?? ""}
                          />
                        </Flex>
                      ))}
                    </Flex>
                    <Text fontSize="xxs" textColor="tertiary">
                      Blank = no limit
                    </Text>
                    {!valid && (
                      <Text fontSize="xs" textColor="error">
                        Enter a whole number from 1 to 1,000,000, or leave the
                        day blank.
                      </Text>
                    )}
                  </Flex>
                ) : (
                  <Text fontSize="xs" textColor="secondary">
                    No daily limit for this warehouse.
                  </Text>
                )}
              </>
            )}
          </Flex>
          {warehouse && (
            <Flex
              align="center"
              borderColor="secondary"
              borderTopWidth="1px"
              gap="lg"
              justify="space-between"
              px="2xl"
              py="lg"
              wrap="wrap"
            >
              {!warehouse.configurationError && (
                <Flex dir="column" gap="xxs">
                  <Flex align="baseline" gap="xs" wrap="wrap">
                    <Text fontSize="xs" fontWeight="medium">
                      {warehouse.count.toLocaleString()}
                      {warehouse.limit === null
                        ? ""
                        : ` / ${warehouse.limit.toLocaleString()}`}{" "}
                      orders today
                    </Text>
                    {warehouse.paused && (
                      <Text fontSize="xs" textColor="warning">
                        · Limit reached
                      </Text>
                    )}
                    {warehouse.limit === null && (
                      <Text fontSize="xs" textColor="tertiary">
                        · No limit
                      </Text>
                    )}
                  </Flex>
                  <Text fontSize="xxs" textColor="tertiary">
                    {getBudgetStatusDetail({
                      warehouse,
                      hasDraft: Boolean(draft),
                    })}
                  </Text>
                </Flex>
              )}
              <Flex gap="sm" justify="flex-end" ml="auto">
                {draft && (
                  <RedoButton
                    disabled={mutation.isPending}
                    hierarchy="tertiary"
                    onClick={discard}
                    size="sm"
                    text="Discard"
                  />
                )}
                <RedoButton
                  disabled={
                    !draft ||
                    !valid ||
                    (enabled && Boolean(warehouse.configurationError))
                  }
                  hierarchy="primary"
                  onClick={() => void save()}
                  pending={mutation.isPending}
                  size="sm"
                  text="Save limit"
                />
              </Flex>
            </Flex>
          )}
        </Flex>
      </StepCard>
    );
  },
);
